import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../api/axios-config";

export interface SubGroup {
  id: number;
  name: string;
  description: string | null;
  parentId: number | null;
  entrepriseId: number;
  depth?: number;
  parent?: { id: number; name: string } | null;
  children?: { id: number; name: string }[];
  _count?: {
    children: number;
    users: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubGroupDto {
  name: string;
  description?: string;
  parentId?: number | null;
  entrepriseId?: number; // Required for ADMIN/SAFEE_STAFF who don't have an associated company
}

export interface UpdateSubGroupDto {
  name?: string;
  description?: string;
}

export interface MoveSubGroupDto {
  newParentId?: number | null;
}

export interface SubGroupTreeNode {
  id: number;
  name: string;
  description: string | null;
  parentId: number | null;
  _count: { users: number; children: number };
  children: SubGroupTreeNode[];
}

export function useSubGroups(entrepriseId?: number) {
  const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubGroups = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setLoading(true);
    try {
      // Pass entrepriseId as query param for ADMIN/SAFEE_STAFF
      const params = entrepriseId ? { entrepriseId } : {};
      const response = await axiosInstance.get<SubGroup[]>("/sub-groups", { params });
      setSubGroups(response.data);
      setError(null);
    } catch (err) {
      if ((err as any).response?.status !== 401) {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [entrepriseId]);

  const createSubGroup = useCallback(async (data: CreateSubGroupDto): Promise<SubGroup | null> => {
    try {
      const response = await axiosInstance.post<SubGroup>("/sub-groups", data);
      await fetchSubGroups();
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchSubGroups]);

  const updateSubGroup = useCallback(async (id: number, data: UpdateSubGroupDto): Promise<SubGroup | null> => {
    try {
      const response = await axiosInstance.patch<SubGroup>(`/sub-groups/${id}`, data);
      await fetchSubGroups();
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchSubGroups]);

  const deleteSubGroup = useCallback(async (id: number): Promise<void> => {
    try {
      await axiosInstance.delete(`/sub-groups/${id}`);
      await fetchSubGroups();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchSubGroups]);

  const moveSubGroup = useCallback(async (id: number, newParentId: number | null): Promise<SubGroup | null> => {
    try {
      const response = await axiosInstance.patch<SubGroup>(`/sub-groups/${id}/move`, {
        newParentId: newParentId,
      });
      await fetchSubGroups();
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchSubGroups]);

  const fetchTree = useCallback(async (): Promise<SubGroupTreeNode[]> => {
    try {
      // Pass entrepriseId as query param for ADMIN/SAFEE_STAFF
      const params = entrepriseId ? { entrepriseId } : {};
      const response = await axiosInstance.get<SubGroupTreeNode[]>("/sub-groups/tree", { params });
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [entrepriseId]);

  useEffect(() => {
    fetchSubGroups();
  }, [fetchSubGroups]);

  return {
    subGroups,
    loading,
    error,
    refetch: fetchSubGroups,
    createSubGroup,
    updateSubGroup,
    deleteSubGroup,
    moveSubGroup,
    fetchTree,
  };
}

export default useSubGroups;
